import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Hero from "@/components/Hero";
import EditorialMarquee from "@/components/Marquee";
import ProductCard from "@/components/ProductCard";
import ProductModal from "@/components/ProductModal";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TILES = [
  {
    cat: "men",
    label: "MEN",
    img: "https://images.unsplash.com/photo-1577221084712-45b0445d2b00?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxtZW4lMjBneW0lMjBob29kaWV8ZW58MHx8fHwxNzkyOTI0OTM3fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    cat: "women",
    label: "WOMEN",
    img: "https://images.unsplash.com/photo-1531520563951-4c0e3d3fcacc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHx3b21lbiUyMGd5bSUyMGxlZ2dpbmdzfGVufDB8fHx8MTc5MjkyNDkzN3ww&ixlib=rb-4.1.0&q=85",
  },
  {
    cat: "accessories",
    label: "ACCESSORIES",
    img: "https://images.unsplash.com/photo-1692506530242-c12d6c3ae2e2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxneW0lMjBiYWclMjBhY2Nlc3Nvcnl8ZW58MHx8fHwxNzkyOTI0OTY2fDA&ixlib=rb-4.1.0&q=85",
  },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios.get(`${API}/products`).then((res) => {
      setFeatured(res.data.products.filter((p) => p.featured));
    }).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      <Hero />
      <EditorialMarquee />

      <section className="mx-auto max-w-[1440px] px-5 md:px-10 py-24 md:py-32">
        <div className="flex items-end justify-between gap-6 mb-12">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-display font-black tracking-tighter leading-none text-4xl md:text-6xl"
          >
            THE <span className="text-[#D3FF24]">ARSENAL</span>
          </motion.h2>
          <Link
            to="/shop"
            data-testid="view-all-products-link"
            className="group hidden sm:inline-flex items-center gap-2 text-xs tracking-[0.25em] text-white/60 hover:text-[#D3FF24] transition-colors"
          >
            VIEW ALL 47 <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} onQuickView={setActive} />
          ))}
        </div>
        <Link
          to="/shop"
          data-testid="view-all-products-mobile"
          className="sm:hidden mt-10 inline-flex items-center gap-2 text-xs tracking-[0.25em] text-[#D3FF24]"
        >
          VIEW ALL 47 <ArrowRight className="h-4 w-4" />
        </Link>
      </section>

      <section className="mx-auto max-w-[1440px] px-5 md:px-10 pb-24 md:pb-32">
        <div className="grid md:grid-cols-3 gap-4 md:gap-6">
          {TILES.map((t, i) => (
            <motion.div
              key={t.cat}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
            >
              <Link
                to={`/shop?cat=${t.cat}`}
                data-testid={`category-tile-${t.cat}`}
                className="group relative block aspect-[3/4] overflow-hidden border border-white/[0.08]"
              >
                <img
                  src={t.img}
                  alt={`${t.label} collection`}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-7 flex items-end justify-between">
                  <span className="font-display font-black text-3xl md:text-4xl tracking-tight">{t.label}</span>
                  <span className="p-3 border border-white/25 text-white group-hover:bg-[#D3FF24] group-hover:text-[#0A0A0C] group-hover:border-[#D3FF24] transition-colors duration-300">
                    <ArrowUpRight className="h-5 w-5" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#111114]">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10 py-24 md:py-32 grid md:grid-cols-2 gap-12 items-center">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-display font-black tracking-tighter leading-[0.95] text-4xl md:text-6xl"
          >
            BUILT IN THE <br />
            <span className="text-outline-blaze">IRON HOUSE.</span>
          </motion.h2>
          <div>
            <p className="text-white/55 text-sm md:text-base leading-relaxed">
              DAFITEXCHANGE started with one rule: if it can't survive leg day, it doesn't ship.
              Every piece is cut for movement, tested under the bar, and priced for people who
              train — not for people who pose.
            </p>
            <div className="grid grid-cols-3 gap-6 mt-10">
              {[
                ["47", "PIECES"],
                ["3", "COLLECTIONS"],
                ["1", "STANDARD"],
              ].map(([n, l]) => (
                <div key={l}>
                  <p className="font-display font-black text-4xl md:text-5xl text-[#D3FF24]">{n}</p>
                  <p className="text-[10px] tracking-[0.3em] text-white/40 mt-2">{l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <ProductModal product={active} onClose={() => setActive(null)} />
      <Footer />
    </main>
  );
}
